import type { DashboardMetric } from '@wlb/shared/api/admin/adminTypes';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Icon, type IconName } from '../../components/ui/Icon';
import { useElementWidth } from '../../hooks/useElementWidth';
import { formatCount } from '../../utils/format';
import './dashboard.css';

type Platform = 'desktop' | 'android' | 'ios';

const PLATFORMS: readonly { id: Platform; labelKey: string; icon: IconName }[] = [
  { id: 'desktop', labelKey: 'dashboard.platform.desktop', icon: 'monitor' },
  { id: 'android', labelKey: 'dashboard.platform.android', icon: 'smartphone' },
  { id: 'ios', labelKey: 'dashboard.platform.ios', icon: 'tablet' },
];

/**
 * Width of one character of the label, and the room its segment loses to
 * padding. The label is set in a small semi-bold face with tabular figures, so
 * every digit is the same width and this is an estimate only for the series
 * name — which is also the part that is allowed to shorten (see `dashboard.css`).
 * Rounded up, because a figure that does not fit must be dropped rather than cut
 * into a different, believable number.
 */
const CHAR_WIDTH = 7.4;
const SEGMENT_PADDING = 12;

const fitsIn = (text: string, available: number): boolean =>
  text.length * CHAR_WIDTH + SEGMENT_PADDING <= available;

interface PlatformBreakdownProps {
  metrics: readonly DashboardMetric[];
}

/**
 * Share-of-platform bars, each segment stating its own platform and figure.
 *
 * The numbers live on the bar rather than in the legend alone: reading a row
 * otherwise meant carrying three colours down the page. The bar stays hidden
 * from assistive technology — the table beside it is the accessible
 * representation, and the exact figures for segments too narrow to be labelled.
 */
export function PlatformBreakdown({ metrics }: PlatformBreakdownProps) {
  const { t } = useTranslation('admin');

  return (
    <div className="dash-platform">
      <ul className="dash-legend">
        {PLATFORMS.map(platform => (
          <li className="dash-legend__item" key={platform.id}>
            <span className={`dash-legend__swatch dash-legend__swatch--${platform.id}`} />
            <Icon name={platform.icon} className="dash-legend__icon" />
            {t(platform.labelKey)}
          </li>
        ))}
      </ul>

      <ul className="dash-platform__list">
        {metrics.map(metric => (
          <PlatformRow key={metric.metrickey ?? metric.metricname ?? ''} metric={metric} />
        ))}
      </ul>
    </div>
  );
}

function PlatformRow({ metric }: { metric: DashboardMetric }) {
  const { t } = useTranslation('admin');
  // Measured rather than assumed: the label is only printed when the segment it
  // belongs to is genuinely wide enough for it.
  const [barRef, barWidth] = useElementWidth<HTMLSpanElement>();
  const total = metric.desktop + metric.android + metric.ios;

  return (
    <li className="dash-platform__row">
      <span className="dash-platform__label">
        {metric.metricname || metric.metrickey || t('common.none')}
      </span>
      <span className="dash-platform__bar" aria-hidden="true" ref={barRef}>
        {total > 0
          ? PLATFORMS.map(platform => {
              const value = metric[platform.id];
              if (value <= 0) {
                return null;
              }
              const figure = formatCount(value);
              const name = t(platform.labelKey);
              const available = (value / total) * barWidth;
              const showFigure = fitsIn(figure, available);
              const showName = fitsIn(`${name} ${figure}`, available);
              return (
                <span
                  className={`dash-platform__segment dash-platform__segment--${platform.id}`}
                  key={platform.id}
                  style={{ width: `${(value / total) * 100}%` }}
                  // Carries the pair for a segment too narrow to print it.
                  title={t('dashboard.platform.segment', { platform: name, value: figure })}>
                  {showName ? <span className="dash-platform__series">{name}</span> : null}
                  {showFigure ? (
                    <span className="dash-platform__figure">{figure}</span>
                  ) : null}
                </span>
              );
            })
          : null}
      </span>
      <span className="dash-platform__total">{formatCount(total)}</span>
    </li>
  );
}
